// Supabase access for clip views / reactions / comments. Schema lives in
// supabase/migrations/20261026070000_clip_engagement.sql. Every call fails soft:
// a missing table or RPC (migration not applied yet) is reported as
// `unavailable` so the viewer can hide the feature instead of breaking.

import { supabase } from '@/lib/supabase';
import {
  isMissingSchemaError,
  type ClipComment,
  type ClipStats,
} from '@/lib/clip-engagement';

const COMMENT_SELECT =
  'id, clip_id, user_id, body, created_at, user:users!clip_comments_user_id_fkey(gamertag, profile_image_url)';

export const COMMENTS_PAGE_SIZE = 100;

export type StatsResult =
  | { available: true; stats: Record<string, ClipStats> }
  | { available: false };

export async function fetchClipStats(clipIds: string[]): Promise<StatsResult> {
  if (clipIds.length === 0) return { available: true, stats: {} };
  try {
    const { data, error } = await supabase
      .from('clip_stats')
      .select('clip_id, view_count, reaction_count, comment_count')
      .in('clip_id', clipIds.slice(0, 200));
    if (error) return { available: false };
    const stats: Record<string, ClipStats> = {};
    for (const row of (data ?? []) as ClipStats[]) stats[row.clip_id] = row;
    return { available: true, stats };
  } catch {
    return { available: false };
  }
}

/** Lifetime view counts from PostHog via /api/clip-views. Empty on failure. */
export async function fetchPostHogClipViews(clipIds: string[]): Promise<Record<string, number>> {
  if (clipIds.length === 0) return {};
  try {
    const ids = [...clipIds].sort().slice(0, 200).join(',');
    const res = await fetch(`/api/clip-views?ids=${encodeURIComponent(ids)}`);
    if (!res.ok) return {};
    const json = (await res.json()) as { views?: Record<string, number> };
    return json.views ?? {};
  } catch {
    return {};
  }
}

/** Clip ids (of the given ones) the signed-in user has hearted. */
export async function fetchMyReactions(clipIds: string[], userId: string): Promise<Set<string>> {
  if (clipIds.length === 0) return new Set();
  try {
    const { data, error } = await supabase
      .from('clip_reactions')
      .select('clip_id')
      .eq('user_id', userId)
      .in('clip_id', clipIds.slice(0, 200));
    if (error) return new Set();
    return new Set((data ?? []).map((r: { clip_id: string }) => r.clip_id));
  } catch {
    return new Set();
  }
}

export async function setClipReaction(clipId: string, userId: string, reacted: boolean): Promise<boolean> {
  try {
    if (reacted) {
      const { error } = await supabase.from('clip_reactions').insert({ clip_id: clipId, user_id: userId });
      // Already hearted (another tab): the end state is what we wanted.
      return !error || error.code === '23505';
    }
    const { error } = await supabase
      .from('clip_reactions')
      .delete()
      .eq('clip_id', clipId)
      .eq('user_id', userId);
    return !error;
  } catch {
    return false;
  }
}

/** True when the view counted (first today, not the owner); null on failure. */
export async function recordClipView(clipId: string): Promise<boolean | null> {
  try {
    const { data, error } = await supabase.rpc('record_clip_view', { p_clip_id: clipId });
    if (error) return null;
    return data === true;
  } catch {
    return null;
  }
}

export type CommentsResult =
  | { status: 'ok'; comments: ClipComment[] }
  | { status: 'unavailable' }
  | { status: 'error' };

function normalizeComment(row: Record<string, unknown>): ClipComment {
  const user = row.user as ClipComment['user'] | ClipComment['user'][] | undefined;
  return {
    id: row.id as string,
    clip_id: row.clip_id as string,
    user_id: row.user_id as string,
    body: row.body as string,
    created_at: row.created_at as string,
    user: Array.isArray(user) ? (user[0] ?? null) : (user ?? null),
  };
}

/** Newest first. */
export async function fetchClipComments(clipId: string): Promise<CommentsResult> {
  try {
    const { data, error } = await supabase
      .from('clip_comments')
      .select(COMMENT_SELECT)
      .eq('clip_id', clipId)
      .order('created_at', { ascending: false })
      .limit(COMMENTS_PAGE_SIZE);
    if (error) return isMissingSchemaError(error) ? { status: 'unavailable' } : { status: 'error' };
    return { status: 'ok', comments: (data ?? []).map((r) => normalizeComment(r as Record<string, unknown>)) };
  } catch {
    return { status: 'error' };
  }
}

export async function fetchClipComment(commentId: string): Promise<ClipComment | null> {
  try {
    const { data, error } = await supabase
      .from('clip_comments')
      .select(COMMENT_SELECT)
      .eq('id', commentId)
      .maybeSingle();
    if (error || !data) return null;
    return normalizeComment(data as Record<string, unknown>);
  } catch {
    return null;
  }
}

export type PostCommentResult =
  | { ok: true; comment: ClipComment }
  | { ok: false; error: { code?: string; message?: string } };

export async function postClipComment(clipId: string, userId: string, body: string): Promise<PostCommentResult> {
  try {
    const { data, error } = await supabase
      .from('clip_comments')
      .insert({ clip_id: clipId, user_id: userId, body })
      .select(COMMENT_SELECT)
      .single();
    if (error || !data) return { ok: false, error: error ?? {} };
    return { ok: true, comment: normalizeComment(data as Record<string, unknown>) };
  } catch (e) {
    return { ok: false, error: { message: e instanceof Error ? e.message : 'failed' } };
  }
}

export async function deleteClipComment(commentId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('delete_clip_comment', { p_comment_id: commentId });
    return !error && data === true;
  } catch {
    return false;
  }
}

export type CommentReportReason = 'inappropriate_content' | 'harassment' | 'other';

/** Files a report into the existing reports flow (Slack #reports). */
export async function reportClipComment(commentId: string, reason: CommentReportReason): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('report_clip_comment', {
      p_comment_id: commentId,
      p_reason: reason,
    });
    return !error && data === true;
  } catch {
    return false;
  }
}

/**
 * New comments on a clip as they're posted. RLS applies per subscriber, so this
 * only delivers comments the viewer could read anyway. Soft deletes by others
 * aren't delivered (the deleted row no longer passes RLS); they drop out on the
 * next fetch.
 */
export function subscribeToClipComments(clipId: string, onInsert: (commentId: string) => void): () => void {
  const channel = supabase
    .channel(`clip_comments:${clipId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'clip_comments', filter: `clip_id=eq.${clipId}` },
      (payload) => {
        const id = (payload.new as { id?: string })?.id;
        if (id) onInsert(id);
      },
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}
